import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { gsap } from 'gsap';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RM } from '../lib/rm';
import { segments } from '../lib/segments';

// Beads visuals load in their own chunk, only when a reply has one.
const OpsBlock = lazy(() => import('./OpsBlock'));

// The twin's markdown-reveal engine — everything TwinMessage (the main app)
// needs to render a streamed reply: per-character accelerating reveal with
// snap-finish, and the code-block copy/download toolbar. Extracted so the
// /aui surface can plug the exact same behavior into assistant-ui's message
// part components instead of duplicating ~200 lines of animation logic.

// ---- Reveal cadence: starts gentle, keeps accelerating (a "yoyo" release,
// not a symmetric bell) so it never slows back down near the end. Longer
// messages compress the whole curve so total reveal time stays bounded
// instead of growing linearly with length. Whatever hasn't caught up by the
// time the reply is fully loaded gets snap-revealed instantly (see the
// reveal ticker below) rather than grinding through at this pace. ----
const MIN_MS = 4; // fastest gap, once accelerated
const MAX_MS = 32; // slowest gap, first characters
const ACCEL = 2.2; // >1 = accelerating (ease-in) rather than linear

function charDelay(i: number, n: number): number {
  const t = n <= 1 ? 1 : i / (n - 1);
  const eased = Math.pow(1 - t, ACCEL);
  const lengthFactor = Math.min(1, 80 / n); // compress the pace for long replies
  return MIN_MS + (MAX_MS - MIN_MS) * eased * lengthFactor;
}

// A rehype step that wraps each *rendered* glyph (post-markdown, so `**` etc.
// never show) in a `<span class="c" data-i="N">`, leaving whitespace as text so
// words still wrap. Code blocks are left whole. `totalRef` receives the count.
type HNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HNode[];
};
function makeSplitChars(totalRef: { current: number }) {
  return () => (tree: HNode) => {
    let idx = 0;
    const walk = (node: HNode, inPre: boolean) => {
      if (!node.children) return;
      const out: HNode[] = [];
      for (const child of node.children) {
        if (child.type === 'text') {
          if (inPre) {
            // Gate code as ONE reveal unit (revealed in order, counted toward
            // total) without per-glyph splitting — preserves monospace, keeps
            // code from popping in ahead of the prose, and stops all-code
            // replies from leaving total=0 (which would never fire onLanded).
            out.push({
              type: 'element',
              tagName: 'span',
              properties: { className: ['c'], 'data-i': String(idx++) },
              children: [child],
            });
            continue;
          }
          // Split into words + whitespace; wrap each word's glyphs in a `.w`
          // (white-space:nowrap) so characters never break mid-word — an
          // inline-block glyph is otherwise a line-break opportunity.
          for (const part of (child.value ?? '').split(/(\s+)/)) {
            if (part === '') continue;
            if (/^\s+$/.test(part)) {
              out.push({ type: 'text', value: part });
              continue;
            }
            const glyphs: HNode[] = Array.from(part).map((ch) => ({
              type: 'element',
              tagName: 'span',
              properties: { className: ['c'], 'data-i': String(idx++) },
              children: [{ type: 'text', value: ch }],
            }));
            out.push({ type: 'element', tagName: 'span', properties: { className: ['w'] }, children: glyphs });
          }
        } else if (child.type === 'element') {
          walk(child, inPre || child.tagName === 'pre' || child.tagName === 'code');
          out.push(child);
        } else {
          out.push(child);
        }
      }
      node.children = out;
    };
    walk(tree, false);
    totalRef.current = idx;
  };
}

// File extension per fenced-code language, so "Download" saves something
// sensible — a ```markdown block becomes an actual .md file, not a .txt.
const LANG_EXT: Record<string, string> = {
  markdown: 'md', md: 'md', text: 'txt', plaintext: 'txt',
  javascript: 'js', typescript: 'ts', jsx: 'jsx', tsx: 'tsx',
  python: 'py', bash: 'sh', shell: 'sh', sh: 'sh', zsh: 'sh',
  json: 'json', yaml: 'yml', yml: 'yml', html: 'html', css: 'css',
  sql: 'sql', go: 'go', rust: 'rs', java: 'java', c: 'c', cpp: 'cpp',
  ruby: 'rb', php: 'php', swift: 'swift', kotlin: 'kt',
};

// Derives a filename from the block's first heading (for markdown) or a
// generic timestamped name, so a saved file isn't just "download.md".
function suggestFilename(text: string, ext: string): string {
  const heading = text.match(/^#{1,3}\s+(.+)$/m)?.[1];
  const slug = heading
    ?.toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'snippet-' + Date.now()}.${ext}`;
}

// Fenced code block: a small toolbar (language, copy, download) above the
// panel. Reads the rendered DOM's textContent rather than the React children
// (which are per-character reveal spans, not plain text) so copy/download
// always get the real content regardless of reveal state.
function CodeBlock({ className, children }: { className?: string; children?: ReactNode }) {
  const codeRef = useRef<HTMLElement>(null);
  const [copied, setCopied] = useState(false);
  const lang = /language-(\w+)/.exec(className || '')?.[1] ?? '';
  const ext = LANG_EXT[lang.toLowerCase()] || 'txt';

  const getText = () => codeRef.current?.textContent ?? '';

  const onCopy = async () => {
    const text = getText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return; // clipboard unavailable (e.g. insecure context) — fail quietly
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const onDownload = () => {
    const text = getText();
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = suggestFilename(text, ext);
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="codeblock">
      <div className="code-toolbar">
        {lang && <span className="code-lang">{lang}</span>}
        <button type="button" onClick={onDownload} title="Download">
          {ext === 'md' ? 'download .md' : 'download'}
        </button>
        <button type="button" onClick={onCopy} title="Copy">
          {copied ? 'copied' : 'copy'}
        </button>
      </div>
      <pre>
        <code ref={codeRef} className={className}>
          {children}
        </code>
      </pre>
    </div>
  );
}

// Links open in a new tab; images lazy-load. `node` is dropped from the DOM spread.
// Fenced code (`pre > code`) gets a copy/download toolbar via CodeBlock; `pre`
// itself is overridden to hand off to it instead of rendering a bare <pre>.
const MD_COMPONENTS: Components = {
  a({ node: _n, ...props }) {
    return <a {...props} target="_blank" rel="noopener noreferrer" />;
  },
  img({ node: _n, ...props }) {
    return <img {...props} loading="lazy" />;
  },
  pre({ node: _n, children }) {
    const codeEl = Array.isArray(children) ? children[0] : children;
    const className =
      codeEl && typeof codeEl === 'object' && 'props' in codeEl
        ? (codeEl as { props?: { className?: string } }).props?.className
        : undefined;
    const codeChildren =
      codeEl && typeof codeEl === 'object' && 'props' in codeEl
        ? (codeEl as { props?: { children?: ReactNode } }).props?.children
        : children;
    return <CodeBlock className={className}>{codeChildren}</CodeBlock>;
  },
};

// Animated cyan squiggle shown while the twin recalls, before the first token.
export function ThinkingIndicator({ label = 'recalling' }: { label?: string }) {
  const pathRef = useRef<SVGPathElement>(null);
  useEffect(() => {
    if (RM) return;
    const tp = pathRef.current!;
    const l = tp.getTotalLength();
    gsap.set(tp, { strokeDasharray: l });
    const tw = gsap.fromTo(tp, { strokeDashoffset: l }, { strokeDashoffset: -l, duration: 1.1, repeat: -1, ease: 'none' });
    return () => {
      tw.kill();
    };
  }, []);
  return (
    <span className="think">
      <svg viewBox="0 0 52 16" width={52} height={16}>
        <path ref={pathRef} d="M2,8 Q9,-2 16,8 T30,8 T44,8 T50,8" />
      </svg>{' '}
      {label}
    </span>
  );
}

// Renders streamed Markdown (links/bold/lists/images), revealing each
// character in place on an accelerating cadence. Reveal is driven
// imperatively (class toggles) so react-markdown re-renders during
// streaming don't restart settled characters. Fires `onLanded` once the
// full reply has appeared. Caller owns the outer wrapper (`.msg`/`.bubble`
// or whatever markup fits) — this renders only the content itself.
export function RevealingContent({
  content,
  streaming,
  searching,
  instant,
  onLanded,
}: {
  content: string;
  streaming: boolean;
  searching?: boolean;
  instant?: boolean;
  onLanded?: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const totalRef = useRef(0);
  const revealedRef = useRef(0);
  const landedRef = useRef(false);
  const streamingRef = useRef(streaming);
  streamingRef.current = streaming;
  const onLandedRef = useRef(onLanded);
  onLandedRef.current = onLanded;

  const splitPlugin = useMemo(() => makeSplitChars(totalRef), []);

  // Reveal ticker: reveal one glyph per accelerating-timed step; the moment the
  // reply is fully loaded (streaming ends) with characters still unrevealed,
  // snap the rest in at once instead of continuing to grind through them one
  // at a time — this both feels snappier and guarantees nothing is left stuck
  // invisible if a mid-stream markdown re-parse ever shifts glyph indices.
  useEffect(() => {
    if (RM || instant) return; // reduced motion / restored history: shown by the layout effect below
    let alive = true;
    let timer: number | undefined;
    const step = () => {
      if (!alive) return;
      const total = totalRef.current;
      if (!streamingRef.current && revealedRef.current < total) {
        const root = rootRef.current;
        if (root) {
          root.querySelectorAll<HTMLElement>('.c').forEach((el) => {
            const i = Number(el.getAttribute('data-i'));
            if (i >= revealedRef.current) el.classList.add('snap', 'shown');
          });
        }
        revealedRef.current = total;
      }
      if (revealedRef.current < total) {
        const i = revealedRef.current;
        const el = rootRef.current?.querySelector<HTMLElement>('.c[data-i="' + i + '"]');
        if (el) el.classList.add('on');
        revealedRef.current = i + 1;
        timer = window.setTimeout(step, charDelay(revealedRef.current, Math.max(total, 1)));
      } else if (!streamingRef.current && total > 0) {
        if (!landedRef.current) {
          landedRef.current = true;
          onLandedRef.current?.();
        }
        return;
      } else {
        timer = window.setTimeout(step, 40); // caught up but still streaming
      }
    };
    step();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Runs after every content render. Reduced motion: show everything. Otherwise
  // re-assert already-revealed glyphs that react-markdown may have re-created,
  // so a settled character never flickers back to hidden.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (RM || instant) {
      root.querySelectorAll<HTMLElement>('.c').forEach((el) => el.classList.add('shown'));
      revealedRef.current = totalRef.current;
      if (!streamingRef.current && totalRef.current > 0 && !landedRef.current) {
        landedRef.current = true;
        onLandedRef.current?.();
      }
      return;
    }
    root.querySelectorAll<HTMLElement>('.c').forEach((el) => {
      const i = Number(el.getAttribute('data-i'));
      if (i < revealedRef.current && !el.classList.contains('on') && !el.classList.contains('shown')) {
        el.classList.add('shown');
      }
    });
  });

  // A reply's ```openui fences draw below its text as Beads visuals; the text keeps the reveal.
  const parts = useMemo(() => segments(content), [content]);
  const text = parts.map((p) => (p.kind === 'md' ? p.text : '')).join('');
  const visuals = parts.filter((p) => p.kind === 'openui');
  const isPending = content.trim() === '' && streaming;

  return (
    <div ref={rootRef}>
      {isPending ? (
        <ThinkingIndicator label={searching ? 'searching the web' : 'recalling'} />
      ) : (
        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[splitPlugin as never]} components={MD_COMPONENTS}>
          {text}
        </Markdown>
      )}
      {visuals.map((v, i) =>
        v.done ? (
          <Suspense key={i} fallback={<div className="visual-slot" />}>
            <OpsBlock src={v.src} turn={v.turn} />
          </Suspense>
        ) : (
          <div key={i} className="visual-slot" />
        ),
      )}
    </div>
  );
}
